import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { History, RotateCcw, Plus, Trash2, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const ACTION = {
  create: { label: "Creato", cls: "text-emerald-600", icon: Plus },
  delete: { label: "Eliminato", cls: "text-red-600", icon: Trash2 },
  restore: { label: "Ripristinato", cls: "text-blue-600", icon: RotateCcw },
};
const ENTITY = {
  cantiere: "Commessa", documento: "Documento", rapportino: "Rapportino",
  fir: "FIR", transazione: "Transazione",
};

function when(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return fmtDate(iso); }
}

export default function Cronologia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api.get("/activity")); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    try {
      await api.post(`/activity/${id}/restore`);
      toast.success("Elemento ripristinato.");
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Cronologia Attività</h1>
          <p className="text-sm text-muted-foreground mt-1">Registro delle azioni svolte nell'app. Visibile solo a te. Puoi ripristinare gli elementi eliminati.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="cronologia-refresh" className="gap-2"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
      </div>

      {loading ? (
        <div className="h-40 rounded-xl bg-secondary animate-pulse" />
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <History className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium">Nessuna attività registrata</p>
          <p className="text-sm text-muted-foreground">Le azioni di creazione ed eliminazione appariranno qui.</p>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="divide-y divide-border p-0">
          {items.map((a) => {
            const act = ACTION[a.action] || { label: a.action, cls: "text-muted-foreground", icon: History };
            const Icon = act.icon;
            return (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3" data-testid={`activity-${a.id}`}>
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-secondary shrink-0 ${act.cls}`}><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">
                    <span className={`font-semibold ${act.cls}`}>{act.label}</span>{" "}
                    <span className="text-muted-foreground">{ENTITY[a.entity_type] || a.entity_type}</span>{" · "}
                    <span className="font-medium">{a.label || "(senza nome)"}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{when(a.at)} · {a.user_name}</p>
                </div>
                {a.action === "delete" && ENTITY[a.entity_type] && (
                  <Button variant="outline" size="sm" className="gap-1.5 shrink-0" data-testid={`activity-restore-${a.id}`} onClick={() => restore(a.id)}>
                    <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                  </Button>
                )}
              </div>
            );
          })}
        </CardContent></Card>
      )}
    </div>
  );
}
